import React, { useMemo, useState } from 'react';
import { Box, Collapse, Divider, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import NearMeIcon from '@mui/icons-material/NearMe';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchSystemIntel } from '../../api/intel';
import { fetchSystemActivity } from '../../api/systems';
import { setWaypoint } from '../../api/waypoint';
import AskDialog from '../../components/AskDialog';
import Chevron from '../../components/Chevron';
import StatusBadge from '../../components/StatusBadge';
import { C, FONT, classColor, elbowTabSx, frameSx, hexToRgba, lcarsLabelSx } from '../../theme/tokens';
import { MapSystem } from '../../types';
import NoteText from './NoteText';
import { unsealSx } from './vaultMotion';
import { parseServerDate } from '../../services/time';

/** Entries with no tag land here. Named rather than blank so an untagged
 * system reads as "filed but unsorted", not as a rendering bug. */
export const UNFILED = 'Uncategorized';

/** Must match AUTO_TAG in src/services/autoTrack.ts — the compartment the
 * presence poller files its own deposits under. */
export const AUTO_TAG = 'auto';

export interface CompartmentHandlers {
  onSelect: (s: MapSystem) => void;
  onRemove: (id: number) => void;
  onTogglePin: (s: MapSystem) => void;
  onToggleFavorite: (s: MapSystem) => void;
  /** '' files the entry back under Uncategorized. */
  onSetTag: (s: MapSystem, tag: string) => void;
  /** Clears an auto-tracked entry's expiry — makes it a curated one. */
  onKeep: (s: MapSystem) => void;
}

/** "expires in 7h" / "expires in 40m" — an auto entry's whole point is that
 * it is temporary, so the row has to say how temporary. */
export function expiresInLabel(expiresAt: string, now = Date.now()): string {
  const ms = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return 'expiring';
  const hours = Math.floor(ms / 3_600_000);
  return hours >= 1 ? `${hours}h left` : `${Math.max(1, Math.round(ms / 60_000))}m left`;
}

/**
 * Systems grouped into compartments by their freeform tag. The grouping IS
 * the vault metaphor: a drawer per purpose (HOME, STAGING, FARM), each with
 * an elbow tab for a label — the one LCARS shape DESIGN.md sanctions, and it
 * is genuinely earned here because a drawer label is what it looks like.
 */
const CompartmentList: React.FC<{
  systems: MapSystem[];
  selectedId: number | null;
  favoriteIds: Set<number>;
  onRenameCompartment: (current: string, next: string) => void;
  /** Rendered directly beneath the selected row — see the note in index.tsx
   * on why the editor lives here rather than in a side rail. */
  renderDetail: (s: MapSystem) => React.ReactNode;
  handlers: CompartmentHandlers;
}> = ({ systems, selectedId, favoriteIds, onRenameCompartment, renderDetail, handlers }) => {
  const compartments = useMemo(() => {
    const byTag = new Map<string, MapSystem[]>();
    for (const s of systems) {
      const key = s.tag?.trim() || UNFILED;
      if (!byTag.has(key)) byTag.set(key, []);
      byTag.get(key)!.push(s);
    }
    // Home compartment first, then alphabetical, with Unfiled always last —
    // the drawer you did not label is the one you look in least.
    return [...byTag.entries()].sort(([a, aRows], [b, bRows]) => {
      const aHome = aRows.some((r) => r.is_home_system);
      const bHome = bRows.some((r) => r.is_home_system);
      if (aHome !== bHome) return aHome ? -1 : 1;
      if (a === UNFILED) return 1;
      if (b === UNFILED) return -1;
      // The auto-tracked drawer churns on its own and is nobody's filing —
      // it sits after the compartments people actually made.
      if (a === AUTO_TAG !== (b === AUTO_TAG)) return a === AUTO_TAG ? 1 : -1;
      return a.localeCompare(b);
    });
  }, [systems]);

  // Every compartment that already exists, offered as one-click targets on
  // each row — retyping a tag by hand was the only way to file an entry, and
  // a typo silently created a second drawer.
  const knownTags = compartments.map(([t]) => t).filter((t) => t !== UNFILED);

  return (
    <Stack spacing={2.5}>
      {compartments.map(([tag, rows], i) => (
        <Compartment
          key={tag}
          tag={tag}
          rows={rows}
          index={i}
          selectedId={selectedId}
          favoriteIds={favoriteIds}
          knownTags={knownTags}
          onRename={onRenameCompartment}
          renderDetail={renderDetail}
          handlers={handlers}
        />
      ))}
    </Stack>
  );
};

const Compartment: React.FC<{
  tag: string;
  rows: MapSystem[];
  index: number;
  selectedId: number | null;
  favoriteIds: Set<number>;
  knownTags: string[];
  onRename: (current: string, next: string) => void;
  renderDetail: (s: MapSystem) => React.ReactNode;
  handlers: CompartmentHandlers;
}> = ({ tag, rows, index, selectedId, favoriteIds, knownTags, onRename, renderDetail, handlers }) => {
  const [open, setOpen] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const isHome = rows.some((r) => r.is_home_system);
  const accent = isHome ? C.wormhole : C.cyan;
  // Uncategorized isn't a compartment, it's the absence of one — renaming it
  // would mean filing every unfiled entry at once, which is not what the
  // pencil next to a drawer label promises. The auto drawer isn't renameable
  // either: the poller would just re-create it under its own name on the
  // next jump, leaving two drawers of the same thing.
  const renameable = tag !== UNFILED && tag !== AUTO_TAG;

  return (
    <Box sx={unsealSx(index)}>
      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{ cursor: 'pointer', mb: 0.75, '&:focus-visible': { outline: `1px solid ${C.cyan}`, outlineOffset: 3 } }}
      >
        <Box sx={{ ...elbowTabSx(accent), display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Chevron color={C.bg} size={7} />
          {tag}
        </Box>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim, pb: 0.25 }}>
          {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
        </Typography>
        {renameable && (
          <IconButton
            size="small"
            title={`Rename "${tag}"`}
            onClick={(e) => {
              e.stopPropagation();
              setRenaming(true);
            }}
            sx={{ color: C.textDim, p: 0.25 }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Stack>

      <AskDialog
        open={renaming}
        title={`Rename "${tag}"`}
        body={`Every system in this group moves with it — ${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}.`}
        defaultValue={tag}
        confirmLabel="Rename"
        onCancel={() => setRenaming(false)}
        onConfirm={(next) => {
          setRenaming(false);
          if (next !== tag) onRename(tag, next);
        }}
      />

      <Collapse in={open} timeout={220}>
        <Stack spacing={1}>
          {rows.map((s) => (
            <Box key={s.id}>
              <EntryRow
                system={s}
                selected={selectedId === s.id}
                favorited={favoriteIds.has(s.solar_system_id)}
                knownTags={knownTags}
                handlers={handlers}
              />
              {selectedId === s.id && (
                <Box sx={{ mt: 1, ...unsealSx() }}>{renderDetail(s)}</Box>
              )}
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

/** One row = at-a-glance intel (class, effect, static summary, last activity)
 * for a saved system — fetched per row. N+1 is fine at small-corp vault sizes;
 * revisit only if this measurably lags.
 *
 * ponytail: auto-tracking raises that ceiling — a long roam can deposit
 * dozens of temporary entries, each paying two requests here. If a busy
 * evening makes the vault crawl, batch it into one
 * `/api/maps/:id/systems?withIntel=1` read rather than optimising the row. */
const EntryRow: React.FC<{
  system: MapSystem;
  selected: boolean;
  favorited: boolean;
  knownTags: string[];
  handlers: CompartmentHandlers;
}> = ({ system, selected, favorited, knownTags, handlers }) => {
  const [tagAnchor, setTagAnchor] = useState<HTMLElement | null>(null);
  const { data: intel } = useQuery({
    queryKey: ['intel', 'system', system.solar_system_id],
    queryFn: () => fetchSystemIntel(system.solar_system_id),
  });
  const { data: activity } = useQuery({
    queryKey: ['system-activity', system.solar_system_id],
    queryFn: () => fetchSystemActivity(system.solar_system_id),
  });

  const navigate = useNavigate();
  const waypointMutation = useMutation({ mutationFn: () => setWaypoint(system.solar_system_id) });

  const staticsSummary = intel?.statics.length
    ? intel.statics.map((s) => s.static_type_code).join(', ')
    : intel?.staticsCatalogued === false
      ? '—'
      : '…';
  const lastActivity = activity?.[0]?.timestamp;
  const accent = selected ? C.wormhole : C.cyan;

  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Box
      onClick={() => handlers.onSelect(system)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handlers.onSelect(system);
        }
      }}
      sx={{
        ...frameSx(accent),
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        borderColor: selected ? hexToRgba(C.wormhole, 0.5) : undefined,
        '&:focus-visible': { outline: `1px solid ${C.cyan}`, outlineOffset: 2 },
      }}
    >
      {/* Wraps rather than forcing a min width: five icon buttons plus the
          name and badges cannot fit 390px on one line, and an unwrappable row
          sets the whole page's width. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
        <Chevron color={accent} pulse={selected} />
        <Typography sx={{ color: C.text, fontFamily: FONT.mono, fontWeight: 600, minWidth: 0 }}>
          {system.label || system.name}
        </Typography>
        {system.label && system.label !== system.name && (
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem' }}>{system.name}</Typography>
        )}
        {system.system_class && <StatusBadge label={system.system_class} accent={classColor(system.system_class)} />}
        {system.is_home_system ? <StatusBadge label="HOME" accent={C.wormhole} /> : null}
        {/* Auto-tracked: the poller put this here because a character flew
            through, and it leaves on its own unless someone keeps it. */}
        {system.auto_expires_at && (
          <>
            <StatusBadge label={`auto · ${expiresInLabel(system.auto_expires_at)}`} accent={C.gold} dim />
            <Box
              component="button"
              type="button"
              title="Keep this entry — stops it expiring"
              onClick={stop(() => handlers.onKeep(system))}
              sx={{
                font: 'inherit',
                fontFamily: FONT.mono,
                fontSize: '0.62rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                px: 0.6,
                py: '1px',
                borderRadius: '3px',
                background: 'transparent',
                border: `1px solid ${hexToRgba(C.gold, 0.5)}`,
                color: C.gold,
                '&:hover': { borderColor: C.gold },
              }}
            >
              keep
            </Box>
          </>
        )}
        <CompartmentChip
          system={system}
          knownTags={knownTags}
          anchor={tagAnchor}
          onOpen={setTagAnchor}
          onClose={() => setTagAnchor(null)}
          onPick={(t) => {
            setTagAnchor(null);
            handlers.onSetTag(system, t);
          }}
        />
        {system.notes_visibility === 'private' && (
          <StatusBadge label="private" accent={C.textDim} dim icon={<LockIcon sx={{ fontSize: 10, color: C.textDim }} />} />
        )}
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }} />
        <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
        <IconButton
          size="small"
          onClick={stop(() => handlers.onToggleFavorite(system))}
          title={favorited ? 'Unstar (personal favorite)' : 'Star (personal favorite)'}
          sx={{ color: favorited ? C.wormhole : C.textDim }}
        >
          {favorited ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
        <IconButton
          size="small"
          onClick={stop(() => handlers.onTogglePin(system))}
          title={system.is_pinned ? 'Unpin (shared)' : 'Pin (shared, visible to everyone with vault access)'}
          sx={{ color: system.is_pinned ? C.gold : C.textDim }}
        >
          {system.is_pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
        </IconButton>
        {/* The system page is addressed by name, which this row only has once
            its intel resolves — disabled until then rather than linking
            somewhere that 404s. */}
        <IconButton
          size="small"
          disabled={!intel}
          onClick={stop(() => intel && navigate(`/system/${encodeURIComponent(intel.system.name)}`))}
          title="View intel"
          sx={{ color: C.textDim }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={stop(() => waypointMutation.mutate())}
          title="Set destination"
          sx={{ color: C.textDim }}
        >
          <NearMeIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={stop(() => handlers.onRemove(system.id))}
          title="Remove from vault"
          sx={{ color: C.textDim }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        </Stack>
      </Stack>

      <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem', mt: 0.75, pl: '26px' }}>
        {intel?.effect?.label ?? '—'} &middot; statics: {staticsSummary} &middot;{' '}
        {lastActivity ? (parseServerDate(lastActivity)?.toLocaleDateString() ?? 'no activity') : 'no activity'}
      </Typography>

      {system.notes_redacted ? (
        <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem'), mt: 0.75, pl: '26px' }}>
          Note classified by its author
        </Typography>
      ) : system.notes ? (
        <Box sx={{ mt: 0.75, pl: '26px' }} onClick={(e) => e.stopPropagation()}>
          <NoteText text={system.notes} />
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * Filing an entry used to mean selecting it, finding the sidebar, and typing
 * the tag by hand — so in practice nothing ever left Uncategorized. This puts
 * the compartment on the row itself: what it's in now, one click to move it.
 */
const CompartmentChip: React.FC<{
  system: MapSystem;
  knownTags: string[];
  anchor: HTMLElement | null;
  onOpen: (el: HTMLElement) => void;
  onClose: () => void;
  onPick: (tag: string) => void;
}> = ({ system, knownTags, anchor, onOpen, onClose, onPick }) => {
  const current = system.tag?.trim() ?? '';
  const [naming, setNaming] = useState(false);
  return (
    <>
      <Box
        component="button"
        type="button"
        title="Move to a compartment"
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          onOpen(e.currentTarget);
        }}
        sx={{
          font: 'inherit',
          fontFamily: FONT.mono,
          fontSize: '0.64rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          px: 0.75,
          py: '1px',
          borderRadius: '3px',
          border: `1px dashed ${hexToRgba(current ? C.cyan : C.textDim, 0.55)}`,
          background: 'transparent',
          color: current ? C.cyan : C.textDim,
          '&:hover': { borderStyle: 'solid' },
        }}
      >
        {current || '+ group'}
      </Box>
      <Menu anchorEl={anchor} open={!!anchor} onClose={onClose} onClick={(e) => e.stopPropagation()}>
        {knownTags
          .filter((t) => t !== current)
          .map((t) => (
            <MenuItem key={t} onClick={() => onPick(t)}>
              {t}
            </MenuItem>
          ))}
        {knownTags.filter((t) => t !== current).length > 0 && <Divider />}
        <MenuItem
          onClick={() => {
            onClose();
            setNaming(true);
          }}
        >
          New group&hellip;
        </MenuItem>
        {current && (
          <MenuItem onClick={() => onPick('')} sx={{ color: C.textDim }}>
            Remove from {current}
          </MenuItem>
        )}
      </Menu>

      <AskDialog
        open={naming}
        title="New group"
        body={`${system.label || system.name} moves into it. Name it after what you use these systems for — home, staging, farm.`}
        defaultValue=""
        placeholder="e.g. staging"
        confirmLabel="Move"
        onCancel={() => setNaming(false)}
        onConfirm={(next) => {
          setNaming(false);
          onPick(next);
        }}
      />
    </>
  );
};

export default CompartmentList;
